import React, { useEffect, useState } from 'react';
import { Box, Select, Button, Popover, SpaceBetween, StatusIndicator } from '@amzn/awsui-components-react';
import { TPEBasicModal, ACTION_TYPE } from 'src/components/shared/TPEBasicModal';
import { WorksheetExecutionGroupDetails } from 'src/models/multidimensional-allocation/WorksheetExecutionGroupDetails';
import { MultiAllocationWorksheetData } from 'src/models/multidimensional-allocation/MultiAllocationWorksheetData';

interface WorksheetExecutionGroupSelectorProps {
    cliExecutionGroups: any[];
    executionGroupDetails?: WorksheetExecutionGroupDetails;
    canEditAttributes: boolean;
    worksheet?: MultiAllocationWorksheetData;
    saveExecutionGroup: (groupNumber: string) => any;
    isSaving: boolean;
}

export default function WorksheetExecutionGroupSelector(props: WorksheetExecutionGroupSelectorProps) {
    const { cliExecutionGroups, executionGroupDetails, canEditAttributes, worksheet, saveExecutionGroup, isSaving } =
        props;

    const options = [{ label: 'Select', value: '' }];
    cliExecutionGroups.forEach((x: string) => options.push({ label: `Group ${x}`, value: `${x}` }));

    const [isEditing, setIsEditing] = useState(false);
    const [selectedOption, setSelectedOption] = useState<any>(options[0]);
    const [showConfirmation, setShowConfirmation] = useState(false);

    useEffect(() => {
        resetDropdown();
    }, [executionGroupDetails]);

    const resetSelectedOption = () => {
        setSelectedOption(options.find((o) => o.value == executionGroupDetails?.executionGroup) || options[0]);
    };

    const resetDropdown = () => {
        resetSelectedOption();
        setIsEditing(executionGroupDetails?.executionGroup == null);
    };

    const selectGroup = (selectedGroup: any) => {
        setSelectedOption(selectedGroup);
        if (selectedGroup == options[0] || selectedGroup.value == executionGroupDetails?.executionGroup) {
            return;
        }
        setShowConfirmation(true);
    };

    const cancelChange = () => {
        setShowConfirmation(false);
        resetDropdown();
    };

    const saveChange = () => {
        setShowConfirmation(false);
        saveExecutionGroup(selectedOption.value);
    };

    const groupSelectorDropdown = (
        <Select
            data-class="basicDropdown"
            disabled={!canEditAttributes || isSaving}
            onChange={({ detail }) => selectGroup(detail.selectedOption)}
            selectedOption={selectedOption}
            options={options}
            placeholder="Select a group"
        />
    );

    const groupWithWorksheetPopover =
        executionGroupDetails?.worksheetId && executionGroupDetails?.executionGroup ? (
            <Popover
                data-class="basicPopover"
                dismissButton={false}
                position="top"
                content={`This execution group for worksheet ${executionGroupDetails?.worksheetId} was updated by user ${executionGroupDetails?.lastUpdateUser} on ${executionGroupDetails?.lastUpdateDate}.`}>
                <span className="smallTextSpan">Group {executionGroupDetails?.executionGroup}</span>
            </Popover>
        ) : null;

    const groupPlainText =
        executionGroupDetails?.executionGroup && !executionGroupDetails?.worksheetId ? (
            <span className="smallTextSpan">Group {executionGroupDetails?.executionGroup}</span>
        ) : null;

    const noGroupAssignedText = !executionGroupDetails?.executionGroup ? (
        <span className="smallTextSpan" style={{ fontStyle: 'italic', color: '#687078' }}>
            No group assigned
        </span>
    ) : null;

    const groupDisplayInViewMode = (
        <Box display="inline-block">{groupWithWorksheetPopover || groupPlainText || noGroupAssignedText}</Box>
    );

    const loadingIndicator = isSaving ? <StatusIndicator type="loading">Saving...</StatusIndicator> : null;

    const editButton =
        !isSaving && canEditAttributes && !isEditing ? (
            <Button
                data-class="smallIconButton"
                variant="icon"
                iconName="edit"
                onClick={() => {
                    setIsEditing(true);
                    resetSelectedOption();
                }}
                ariaLabel="Edit group assignment"
            />
        ) : null;

    const actionControl = loadingIndicator || editButton;

    return (
        <React.Fragment>
            <Box>
                <SpaceBetween direction="horizontal" size="s" alignItems="center">
                    <Box display="inline-block" margin={{ right: 's' }}>
                        <strong>Assign Worksheet to a Group:</strong>
                    </Box>

                    {isEditing ? groupSelectorDropdown : groupDisplayInViewMode}

                    {actionControl}
                </SpaceBetween>
            </Box>
            <TPEBasicModal
                className="updateExecutionGroupConfirmationModal"
                visible={showConfirmation}
                action={ACTION_TYPE.CONFIRM_CANCEL}
                title="Assign execution group confirmation"
                onCancel={cancelChange}
                onConfirm={saveChange}>
                <div>
                    This will assign this worksheet to {selectedOption.label}.
                    {executionGroupDetails?.totalLinkedCalculations &&
                        executionGroupDetails.totalLinkedCalculations > 0 && (
                            <span>
                                {' '}
                                This worksheet is linked to {executionGroupDetails.totalLinkedCalculations}{' '}
                                calculations.
                            </span>
                        )}
                    {executionGroupDetails?.totalAffectedCalculations &&
                        executionGroupDetails.totalAffectedCalculations > 0 && (
                            <span>
                                {' '}
                                {executionGroupDetails.totalAffectedCalculations} calculation(s) will be moved to draft.
                            </span>
                        )}{' '}
                    Confirm to proceed.
                </div>
            </TPEBasicModal>
        </React.Fragment>
    );
}
